import { Avatar, Button, DialogCard } from '@/components/ui'
import type { ConsultationProtocolUsage } from '@rezeta/shared'
import { formatRelativeMinutes } from '@/lib/format/dates'
import { resumeBannerStrings } from './strings'

export interface ResumeBannerProps {
  usage: ConsultationProtocolUsage
  patientName: string
  patientAge?: number
  currentStep?: { number: number; title: string }
  totalSteps?: number
  completedSteps?: number
  lastEditField?: string
  lastEditTime?: string
  elapsedMinutes: number
  onResume: () => void
  onStartNew: () => void
}

export function ResumeBanner({
  usage,
  patientName,
  patientAge,
  currentStep,
  totalSteps,
  completedSteps,
  lastEditField,
  lastEditTime,
  elapsedMinutes,
  onResume,
  onStartNew,
}: ResumeBannerProps): JSX.Element {
  const initials = patientName
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <DialogCard
      width="xl"
      elevation="raised"
      overline={resumeBannerStrings.overline}
      overlineTone="neutral"
      title={resumeBannerStrings.title}
      description={
        <>{resumeBannerStrings.description(patientName, formatRelativeMinutes(elapsedMinutes))}</>
      }
      footer={
        <div className="flex flex-col w-full gap-3">
          <div className="flex items-center gap-2 w-full">
            <Button variant="primary" size="md" className="flex-1" onClick={onResume}>
              {currentStep
                ? resumeBannerStrings.resumeButton(currentStep.number, currentStep.title)
                : resumeBannerStrings.resumeButtonSimple}
            </Button>
            <Button variant="secondary" size="md" onClick={onStartNew}>
              {resumeBannerStrings.startNewButton}
            </Button>
          </div>
          <p className="text-overline text-n-400 text-center">
            {resumeBannerStrings.draftRetentionNote}
          </p>
        </div>
      }
    >
      <div className="border border-n-200 rounded p-4 bg-n-25">
        <div className="flex items-center gap-3 mb-3">
          <Avatar initials={initials} size="default" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-n-900 truncate">
              {patientName}
              {patientAge != null && resumeBannerStrings.patientAge(patientAge)}
            </div>
            {currentStep && (
              <div className="text-overline text-n-500 mt-px">
                {resumeBannerStrings.protocolStep(
                  usage.protocolTitle,
                  currentStep.number,
                  totalSteps ?? '?',
                )}
              </div>
            )}
          </div>
        </div>

        {totalSteps != null && completedSteps != null && (
          <div className="flex gap-1 mb-3">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={
                  i < completedSteps
                    ? 'h-1 flex-1 bg-p-500 rounded-full'
                    : 'h-1 flex-1 bg-n-100 rounded-full'
                }
              />
            ))}
          </div>
        )}

        {(lastEditField || lastEditTime) && (
          <div className="flex items-center justify-between text-overline text-n-500">
            {lastEditField && (
              <span>
                {resumeBannerStrings.lastEditLabel}{' '}
                <span className="text-n-800 font-medium">{lastEditField}</span>
              </span>
            )}
            {lastEditTime && (
              <span>
                {lastEditTime} {resumeBannerStrings.autoSaved}
              </span>
            )}
          </div>
        )}
      </div>
    </DialogCard>
  )
}
